/**
 * Bangabda (Bengali) and Hijri conversion, shared by the age calculator and the
 * date-format tool.
 *
 * Bangabda follows the Bangladesh Academy 2019 reform, where every month starts
 * on a fixed Gregorian date: Boishakh 1 = 14 April, and the year is CE − 593 on
 * or after Boishakh 1 (CE − 594 before it). The first five months have 31 days
 * and the rest 30, with Falgun taking a 31st day in a Gregorian leap year — the
 * fixed month starts below already encode that, so no leap branch is needed.
 *
 * Do NOT use `Intl` with `calendar: 'indian'` for this: that is the Indian
 * national (Saka) calendar — year CE − 78, months starting ~a week earlier —
 * which only looks Bengali because `bn-BD` translates the Saka month names.
 */

const BANGLA_MONTHS_BN = [
    'বৈশাখ', 'জ্যৈষ্ঠ', 'আষাঢ়', 'শ্রাবণ', 'ভাদ্র', 'আশ্বিন',
    'কার্তিক', 'অগ্রহায়ণ', 'পৌষ', 'মাঘ', 'ফাল্গুন', 'চৈত্র',
];

const BANGLA_MONTHS_EN = [
    'Baishakh', 'Jyaistha', 'Asharh', 'Shraban', 'Bhadra', 'Ashwin',
    'Kartik', 'Agrahayan', 'Poush', 'Magh', 'Falgun', 'Chaitra',
];

const HIJRI_MONTHS_AR = [
    'محرم', 'صفر', 'ربيع الأول', 'ربيع الثاني', 'جمادى الأولى', 'جمادى الثانية',
    'رجب', 'شعبان', 'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة',
];

const HIJRI_MONTHS_BN = [
    'মুহররম', 'সফর', 'রবিউল আউয়াল', 'রবিউস সানি',
    'জমাদিউল আউয়াল', 'জমাদিউস সানি',
    'রজব', 'শাবান', 'রমজান', 'শাওয়াল', 'জিলকদ', 'জিলহজ',
];

const HIJRI_MONTHS_EN = [
    'Muharram', 'Safar', 'Rabi al-Awwal', 'Rabi al-Thani',
    'Jumada al-Awwal', 'Jumada al-Thani',
    'Rajab', "Sha'ban", 'Ramadan', 'Shawwal', "Dhul Qa'dah", 'Dhul Hijjah',
];

/** Every Bangla month start, as (month index, Gregorian month/day). */
const MONTH_STARTS = [
    { month: 1, gMonth: 3, gDay: 14 },  // Boishakh — 14 April
    { month: 2, gMonth: 4, gDay: 15 },  // Jyoishtho — 15 May
    { month: 3, gMonth: 5, gDay: 15 },  // Asharh — 15 June
    { month: 4, gMonth: 6, gDay: 16 },  // Srabon — 16 July
    { month: 5, gMonth: 7, gDay: 16 },  // Bhadro — 16 August
    { month: 6, gMonth: 8, gDay: 16 },  // Ashwin — 16 September
    { month: 7, gMonth: 9, gDay: 16 },  // Kartik — 16 October
    { month: 8, gMonth: 10, gDay: 15 }, // Ogrohayon — 15 November
    { month: 9, gMonth: 11, gDay: 15 }, // Poush — 15 December
    { month: 10, gMonth: 0, gDay: 14 }, // Magh — 14 January
    { month: 11, gMonth: 1, gDay: 13 }, // Falgun — 13 February
    { month: 12, gMonth: 2, gDay: 15 }, // Choitro — 15 March
];

const DAY_MS = 86_400_000;

/** Midnight local, so the day difference is never skewed by a time component. */
function atMidnight(year, monthIndex, day) {
    const d = new Date(year, monthIndex, day);
    d.setHours(0, 0, 0, 0);
    return d;
}

/**
 * @param {Date} date
 * @returns {{ year: number, month: number, day: number, monthBn: string, monthEn: string } | null}
 */
export function toBanglaDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

    const gYear = date.getFullYear();
    const target = atMidnight(gYear, date.getMonth(), date.getDate());

    // Candidates run from Poush of the previous Bangla year through Poush of this
    // one, so a January date resolves against the previous year's months.
    const candidates = [];
    for (const m of MONTH_STARTS) {
        // Magh, Falgun and Choitro fall in the Gregorian year AFTER Boishakh.
        const isLateMonth = m.month >= 10;
        candidates.push({
            month: m.month,
            year: (isLateMonth ? gYear - 594 : gYear - 593),
            start: atMidnight(gYear, m.gMonth, m.gDay),
        });
        if (isLateMonth) continue;
        // The same month one Gregorian year earlier, so dates before 14 January
        // (still Poush of the previous Bangla year) resolve correctly.
        candidates.push({
            month: m.month,
            year: gYear - 594,
            start: atMidnight(gYear - 1, m.gMonth, m.gDay),
        });
    }
    candidates.sort((a, b) => a.start - b.start);

    let current = candidates[0];
    for (const candidate of candidates) {
        if (candidate.start <= target) current = candidate;
        else break;
    }

    return {
        year: current.year,
        month: current.month,
        day: Math.round((target - current.start) / DAY_MS) + 1,
        monthBn: BANGLA_MONTHS_BN[current.month - 1],
        monthEn: BANGLA_MONTHS_EN[current.month - 1],
    };
}

/**
 * Hijri via the Umm al-Qura calendar — the civil reference most calendars print.
 * Bangladesh sights the moon locally and can run a day later; treat this as ±1.
 * @param {Date} date
 * @returns {{ year: number, month: number, day: number, monthAr: string, monthBn: string, monthEn: string } | null}
 */
export function toHijriDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

    try {
        const parts = new Intl.DateTimeFormat('en-US-u-ca-islamic-umalqura', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
        }).formatToParts(date).reduce((acc, part) => {
            acc[part.type] = part.value;
            return acc;
        }, {});
        const month = Number(parts.month);
        if (!month) return null;
        return {
            year: Number(String(parts.year).replace(/\D/g, '')),
            month,
            day: Number(parts.day),
            monthAr: HIJRI_MONTHS_AR[month - 1],
            monthBn: HIJRI_MONTHS_BN[month - 1],
            monthEn: HIJRI_MONTHS_EN[month - 1],
        };
    } catch {
        return null;
    }
}

export { BANGLA_MONTHS_BN, BANGLA_MONTHS_EN, HIJRI_MONTHS_AR, HIJRI_MONTHS_BN, HIJRI_MONTHS_EN };
